/**
 * The only place this app talks to the server.
 *
 * Everything goes through `api()` so three things are handled once rather than at
 * forty call sites: the session cookie is sent, a non-2xx response becomes a
 * thrown `ApiError` carrying the server's own message, and 422 field errors stay
 * attached so a form can show them next to the offending input.
 *
 * The server's messages are written to be read by an FDE ("no outbound channel is
 * configured for agent 698 — add a route on the Routing page"), so the UI shows
 * them verbatim instead of replacing them with "Something went wrong".
 */

export type FieldError = { field: string; message: string };

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly errors: FieldError[] = [],
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** True when the user is simply not signed in, which is not an error to show. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: "same-origin",
    headers: {
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...init?.headers,
    },
  });

  const text = await res.text();
  const body: unknown = text ? safeJson(text) : null;

  if (!res.ok) {
    const record = (body ?? {}) as { error?: string; errors?: FieldError[] };
    throw new ApiError(
      res.status,
      record.error ?? `${res.status} ${res.statusText}`,
      record.errors ?? [],
    );
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    // An HTML error page from a proxy, most likely. Keep the first line — it is
    // more useful than "unexpected token < in JSON".
    return { error: text.split("\n")[0]?.slice(0, 200) };
  }
}

export const post = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });

export const patch = <T>(path: string, body: unknown) =>
  api<T>(path, { method: "PATCH", body: JSON.stringify(body) });

export const put = <T>(path: string, body: unknown) =>
  api<T>(path, { method: "PUT", body: JSON.stringify(body) });

export const del = <T>(path: string) => api<T>(path, { method: "DELETE" });

/** Turn any thrown value into something safe to render. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Unexpected error.";
}

// ------------------------------------------------------------------ types ----
//
// Hand-written rather than generated: the server is in the same repo and these
// stay honest because the pages fail to compile when a field is renamed. Every
// shape below was read off a live response, not guessed.

export type SessionUser = {
  id: string;
  email: string;
  name: string | null;
  is_admin: boolean;
};

export type QueueGroup = "due" | "in_flight" | "called" | "attention" | "failed" | "closed";

/** Mirrors the server's `QUEUE_ORDER_BY` — the only two sortable columns. */
export type QueueOrderBy = "when" | "updated_at";
export type QueueOrderDir = "asc" | "desc";

export type QueueSummary = {
  byStatus: Record<string, number>;
  byGroup: Record<QueueGroup, number>;
  byAgent: { agent_id: string | null; agent_name: string | null; n: number }[];
  byChannel: { channel_id: string | null; n: number }[];
  byOutcome: { outcome: string; n: number }[];
  dueNow: number;
  nextDueAt: string | null;
};

export type Account = {
  id: string;
  slug: string;
  name: string;
  base_url: string;
  tenant_host: string;
  timezone: string;
  default_country_code: string | null;
  default_hour: number;
  part_of_day_hours: Record<"morning" | "afternoon" | "evening" | "night", number>;
  no_time_delay_minutes: number;
  min_lead_minutes: number;
  max_defer_days: number;
  max_horizon_days: number;
  retry_delay_minutes: number;
  max_attempts_per_channel: number;
  attempt_lookback_days: number;
  verify_first_delay_seconds: number;
  verify_interval_seconds: number;
  verify_max_checks: number;
  paused: boolean;
  daily_dial_cap: number | null;
  /**
   * When the agent-channel cache was last fetched from 8loop, and what went wrong.
   * Routing cannot resolve anything until this has succeeded once, so it is a setup
   * step in its own right rather than an implementation detail.
   */
  channels_refreshed_at: string | null;
  channels_refresh_error: string | null;
  created_at: string;
  updated_at: string;
};

export type AccountWithQueue = Account & {
  queue: QueueSummary;
  credentials: CredentialSummary;
  /** Zero means nothing will ever be dialled, however full the queue is. */
  calling_window_count: number;
};

export type MeResponse = {
  user: SessionUser | null;
  accounts?: Account[];
  dial_mode: string;
  dry_run: boolean;
};

export type CallingWindow = { weekday: number; start: string; end: string };

/**
 * One override: "callbacks from THIS channel go somewhere else."
 *
 * There is no row for the normal case. A callback dials back on the agent channel the
 * conversation came from, so an empty list means routing is working, not unconfigured.
 * The agent, outcome, priority, retry-delay and attempt-cap fields this type used to
 * carry are gone — retry policy lives once on the account and, where it varies, per
 * outcome.
 */
export type ChannelRoute = {
  id: string;
  account_id: string;
  /** The channel being overridden. Exactly one row per channel. */
  source_channel_id: string;
  /**
   * Names captured when the override was saved, so it reads in words even when the
   * credential has since expired and the live channel list is unavailable — which is
   * exactly when someone is here trying to work out why nothing dialled.
   */
  source_agent_name: string | null;
  source_channel_label: string | null;
  dial_routing_identifier: string;
  dial_agent_id: string | null;
  dial_agent_name: string | null;
  dial_channel_label: string | null;
  enabled: boolean;
};

export type OutcomeRule = {
  outcome: string;
  label: string;
  enabled: boolean;
  delay_minutes: number | null;
  retry_delay_minutes: number | null;
  max_attempts: number | null;
  suggested_delay_minutes: number;
  never_dialled: boolean;
};

export type FieldMapping = {
  id: string;
  /**
   * The dialled agent this field is for; null means every agent on the account.
   *
   * Must be round-tripped by anything that saves a mapping. The upsert keys on
   * `(account_id, agent_id, field_name)`, so dropping it while toggling `enabled`
   * does not edit the row — it creates a second, account-wide one alongside it.
   */
  agent_id: string | null;
  field_name: string;
  source_kind: "literal" | "payload" | "builtin";
  source_value: string;
  enabled: boolean;
};

export type ContextBuiltin = { value: string; label: string; description: string };

export type CredentialSummary = {
  /**
   * Two states, not three. There used to be a `"password"` mode, for an account
   * authenticating with an 8loop email and password; it is gone — API key only.
   */
  mode: "api_key" | "none";
  api_key_masked: string | null;
  supports_idempotency: boolean;
  has_ingest_token: boolean;
  /** Whether the app can read the token back and embed it itself, or has to ask for it. */
  ingest_token_readable: boolean;
  credential_error: string | null;
  /**
   * The last time anyone asked 8loop whether this credential works, and what it said.
   *
   * `last_check_at: null` means **never asked**, which is a different thing from
   * working. Without this distinction a setup checklist can only report that
   * *something* is stored — and this repo's own dev account spent weeks showing a
   * confident "Email and password" over a password 8loop had been 401-ing all along.
   */
  last_check_at: string | null;
  last_check_ok: boolean | null;
  last_check_error: string | null;
};

export type AccountConfig = {
  account: Account;
  credentials: CredentialSummary;
  calling_windows: CallingWindow[];
  routes: ChannelRoute[];
  outcome_rules: OutcomeRule[];
  field_mappings: FieldMapping[];
  context_builtins: ContextBuiltin[];
  queue: QueueSummary;
  ingest_url: string;
  dial_mode: string;
  dry_run: boolean;
};

export type Callback = {
  id: string;
  phone_number: string;
  contact_name: string | null;
  outcome: string;
  status: string;
  source_agent_id: string | null;
  source_agent_name: string | null;
  source_channel_id: string | null;
  requested_at: string | null;
  requested_raw: string | null;
  scheduled_for: string | null;
  schedule_reason: string | null;
  attempts: number;
  dial_routing_identifier: string | null;
  dial_channel_label: string | null;
  eightloop_call_id: string | null;
  last_end_reason: string | null;
  dial_outcome: string | null;
  reported_outcome: string | null;
  attention_reason: string | null;
  last_error_message: string | null;
  verify_after: string | null;
  created_at: string;
  updated_at: string;
  /** Server-rendered, in the account's timezone — never re-derive these locally. */
  phone_display: string;
  outcome_label: string;
  scheduled_for_local: string | null;
  requested_at_local: string | null;
  updated_at_local: string;
  overdue: boolean;
  account_timezone: string;
};

export type CallbackListResponse = {
  callbacks: Callback[];
  total: number;
  limit: number;
  offset: number;
  /** The window the server actually applied, echoed back. Either end may be open. */
  from: string | null;
  to: string | null;
  /**
   * The order the server actually used. Echoed rather than assumed because the
   * default direction depends on the tab — pending work reads soonest-first —
   * and a header arrow drawn from a client-side guess would eventually point the
   * wrong way.
   */
  order_by: QueueOrderBy;
  order_dir: QueueOrderDir;
  summary: QueueSummary;
  facets: {
    agents: { agent_id: string | null; agent_name: string | null; n: number }[];
    channels: { channel_id: string | null; n: number }[];
    outcomes: { value: string; label: string; n: number }[];
    groups: { value: QueueGroup; count: number }[];
  };
};

export type CallbackAttempt = {
  attempt_no: number;
  idempotency_key: string;
  started_at: string;
  finished_at: string | null;
  ok: boolean;
  http_status: number | null;
  eightloop_call_id: string | null;
  error_code: string | null;
  error_message: string | null;
  idempotent_replay: boolean;
  request_body: unknown;
};

export type IngestEvent = {
  id: string;
  received_at: string;
  accepted: boolean;
  rejected_reason: string | null;
  payload: Record<string, unknown> | null;
};

export type CallbackDetail = {
  callback: Callback & {
    context: Record<string, string> | null;
    route_id: string | null;
    /** True when this channel was pointed somewhere else by hand on the Routing tab. */
    dial_is_override: boolean;
  };
  attempts: CallbackAttempt[];
  events: IngestEvent[];
  history: Callback[];
  retry: {
    delay_minutes: number;
    max_attempts: number;
    lookback_days: number;
    delay_from: string;
    max_attempts_from: string;
  };
};

/**
 * One agent on the tenant, as `GET /eightloop/agents` describes it.
 *
 * Channels are NOT nested here. They arrive as a flat sibling array keyed by
 * `agent_id`, because 8loop returns them from a separate endpoint and a channel
 * can outlive the agent list it would have been nested under — six of equentis's
 * fifteen channels belong to archived agents that the picker deliberately hides.
 *
 * This type used to claim `channels` was nested. It was never true, and reading
 * `agents[].channels` white-screened the whole app the moment anyone opened the
 * route editor on an account whose credential actually worked.
 */
export type EightloopAgent = {
  id: string;
  name: string;
  primary_language: string | null;
  /** Null means nothing is published, so the agent cannot run a conversation. */
  active_version: string | null;
  archived: boolean;
};

/**
 * One agent channel, with the app's dialability verdict already applied.
 *
 * `dialable` is false only where a *synchronous* 8loop precondition is known to
 * fail, so a channel is never called unusable on the strength of a permission the
 * key does not have. `warnings` is the other half: dialable, but you probably
 * don't want it — an archived agent, or a TCN campaign config that makes 8loop
 * answer 202 and then drop the call.
 */
export type EightloopChannel = {
  id: string;
  agent_id: string;
  /** Denormalised by the server so no screen has to print a bare id. */
  agent_name: string | null;
  /** The channel's own name in 8loop. Often the same as the agent's. */
  display_name: string | null;
  call_direction: string | null;
  call_initiator: string | null;
  /** What `POST /calls/` actually dials by — not the channel id. */
  routing_identifier: string | null;
  /**
   * The number the customer sees. Worth showing everywhere a channel is chosen:
   * on equentis the two AGENT-initiated channels for the feedback agents dial
   * from a Plivo preview line, not the production number the campaign uses.
   */
  caller_id: string | null;
  phone_number_display_name: string | null;
  provider_type: string | null;
  dialable: boolean;
  blocked_reason: string | null;
  blocked_code: string | null;
  warnings: string[];
};

/**
 * One conversation field on one agent, as this app cached it.
 *
 * The two booleans are the whole reason this type exists. `settable_at_start` is the
 * only thing that decides whether a name may appear in `init_values.conversation_fields`
 * — 8loop rejects the entire call otherwise — and `written_by_analysis` is the other
 * half: on every agent measured the two sets are disjoint, so the fields a callback can
 * *receive* and the fields post-call analysis *produces* never share a name. That is why
 * context is a source → target map instead of a passthrough.
 */
export type AgentField = {
  agent_id: string;
  field_name: string;
  /** STRING | ENUM | INTEGER | FLOAT | DATE | DATETIME | BOOLEAN, as 8loop reports it. */
  field_type: string | null;
  /** Doubles as 8loop's extraction prompt, so it is the best label the field has. */
  description: string | null;
  /** Non-empty only for ENUM. */
  allowed_values: string[];
  settable_at_start: boolean;
  written_by_analysis: boolean;
  archived: boolean;
  refreshed_at: string;
};

/** Whether we could read one agent's fields, and what we found. */
export type AgentFieldRefresh = {
  agent_id: string;
  agent_name: string | null;
  refreshed_at: string;
  /** Non-null means the last attempt failed and these rows are stale, not absent. */
  error: string | null;
  field_count: number;
  settable_count: number;
};

export type AgentFieldsResponse = {
  fields: AgentField[];
  /**
   * Per-agent refresh record. Needed to tell "this agent has no settable fields" apart
   * from "we were never able to ask" — those send an FDE to completely different places.
   */
  refreshes: AgentFieldRefresh[];
};

export type EightloopAgentsResponse = {
  /** Live agents only — what a picker should offer. */
  agents: EightloopAgent[];
  /** Including archived, so an existing route can still be named. */
  all_agents: EightloopAgent[];
  channels: EightloopChannel[];
  /**
   * False when the key lacks `MANAGE_AGENTS`, so the caller-ID and
   * telephony-integration checks were skipped rather than passed.
   */
  checked_phone_numbers: boolean;
};

/**
 * Where one channel's callbacks go, and whether that target can actually dial.
 *
 * `how` is the whole answer: `same_channel` is the default and needs no configuration,
 * `override` means a row redirects it, `unroutable` means nothing can be dialled and
 * `unroutable_reason` says which fix applies. There is no ranking left to explain —
 * the old resolver picked the most specific of several wildcard rules and broke ties on
 * priority, so a rule sitting third could beat the first, and no layout made that legible.
 */
export type ChannelDial = {
  how: "same_channel" | "override" | "unroutable";
  /** Present only for an override — the row to edit or delete. */
  route_id: string | null;
  routing_identifier: string | null;
  channel_label: string | null;
  agent_name: string | null;
  /**
   * The agent that will ANSWER the callback — not the one that sent it. Conversation
   * fields are agent-scoped and only settable at call start on the agent that declares
   * them, so this is the id every field picker on a preview has to key on.
   */
  dial_agent_id: string | null;
  /** Set when `how` is `unroutable`: what to do about it, in words. */
  unroutable_reason: string | null;
  /** An override that points back at its own channel, i.e. does nothing. */
  redundant: boolean;
  /** Null means not checked (the key cannot read phone numbers) — never "fine". */
  caller_id: string | null;
  provider_type: string | null;
  dialable: boolean | null;
  blocked_reason: string | null;
  warnings: string[];
  /** The target points at a routing identifier the tenant no longer has. */
  missing_from_tenant: boolean;
};

export type RoutedChannel = {
  channel_id: string;
  agent_id: string | null;
  agent_name: string | null;
  channel_label: string | null;
  caller_id: string | null;
  provider_type: string | null;
  /** False for a channel that only exists in our own history — deleted in 8loop since. */
  live: boolean;
  /** An inbound channel never starts an outbound call, so it never sources a callback. */
  inbound_only: boolean;
  /** The agent is archived, so it will never send another callback. */
  agent_archived: boolean;
  callbacks_seen: number;
  /** An override exists but is switched off, so the identity default is in force. */
  override_disabled: boolean;
  dial: ChannelDial;
};

export type RoutesResolutionResponse = {
  channels: RoutedChannel[];
  /**
   * When the channel list was last fetched from 8loop. This screen reads a local cache
   * so it still renders with a dead credential, so its age is stated rather than hidden.
   */
  refreshed_at: string | null;
  refresh_error: string | null;
  checked_phone_numbers: boolean;
};

/**
 * What `POST /ingest/preview` answers. `details` mirrors the server's `PreviewBody`
 * field for field rather than being `Record<string, unknown>`, because the dry-run
 * screen reads `conversation_fields` out of it to say whether a channel would carry any
 * context — and off an untyped record that read compiles while being `unknown`.
 */
export type PreviewResponse = {
  would: string;
  result: string;
  message: string;
  errors?: FieldError[];
  details?: {
    phone_number?: string;
    phone_number_note?: string;
    outcome?: string;
    requested_at?: string | null;
    requested_precision?: string | null;
    time_read_as?: string;
    scheduled_for?: string | null;
    scheduled_for_local?: string | null;
    schedule_reason?: string;
    dial_routing_identifier?: string | null;
    dial_channel_label?: string | null;
    route_matched?: string;
    conversation_fields?: Record<string, string>;
    retry?: { delay_minutes: number; max_attempts: number; lookback_days: number; from: string };
  };
};

// ------------------------------------------- the agents an account does callbacks for ---

/**
 * One agent on the account's callback list, with everything the Agents tab needs to answer
 * "is this one done?" — all of it from the app's own tables, so the list renders without a
 * single 8loop call and still renders when the tenant's API key has expired.
 */
export type CallbackAgent = {
  agent_id: string;
  agent_name: string | null;
  note: string | null;
  added_at: string;
  /** `checked_at: null` means nobody has looked yet — not the same as "not installed". */
  hook: {
    present: boolean | null;
    version: string | null;
    /**
     * The draft our hook is sitting on, unpublished — so it exists on the agent and runs on
     * no call at all. A third state, and the reason it is not a flavour of `present`: a tick
     * would say the job is done while every finished call goes nowhere, and a cross would
     * send somebody to install what is already installed.
     */
    staged_version: string | null;
    webhook_id: string | null;
    checked_at: string | null;
    error: string | null;
  };
  channels: { total: number; dialable: number; overridden: number; none_can_dial: boolean };
  context_fields: number;
  /** null means this agent's conversation fields have never been fetched. */
  settable_fields: number | null;
  callbacks_seen: number;
  archived: boolean;
  missing_from_tenant: boolean;
};

/** An agent that could be added. `blocked_reason` is shown on the row, never used to hide it. */
export type AgentCandidate = {
  agent_id: string;
  agent_name: string | null;
  channels: number;
  dialable_channels: number;
  archived: boolean;
  callbacks_seen: number;
  blocked_reason: string | null;
};

/** An agent sending callbacks that nobody added. Surfaced, never dropped. */
export type UnlistedAgent = {
  agent_id: string;
  agent_name: string | null;
  callbacks_seen: number;
  last_seen_at: string;
};

export type CallbackAgentsResponse = {
  agents: CallbackAgent[];
  candidates: AgentCandidate[];
  unlisted: UnlistedAgent[];
  /** null means the channel list has never been fetched, so there are no candidates to offer. */
  channels_refreshed_at: string | null;
  channels_refresh_error: string | null;
};

// ------------------------------------------------------ the post-call code action ---

/**
 * The server's `CodeActionPlan`, which is the whole pre-flight for one agent: which of
 * its conversation fields play which role, what still has to be created, what the
 * generated script will name, and what is true and worth saying before anybody installs.
 *
 * Mirrored field for field rather than typed as a record, because the screen reads
 * `roles.disposition.confidence` and `draft.agent_has_published_version` to decide what
 * to show — and off an untyped record those reads compile while being `unknown`.
 */
export type CodeActionRoleMatch = {
  role: string;
  field: {
    id?: string;
    name: string;
    type?: string | null;
    field_type?: string | null;
    description?: string | null;
    allowed_values?: string[] | null;
    set_on_conversation_start?: boolean;
    re_extract_on_conversation_end?: boolean;
  };
  why: string;
  confidence: "exact" | "likely" | "possible";
};

export type CodeActionProposedField = {
  role: "callback_time" | "disposition";
  name: string;
  type: string;
  description: string;
  allowed_values?: string[];
  set_on_conversation_start: boolean;
  re_extract_on_conversation_end: boolean;
  why: string;
  recommended: boolean;
};

export type CodeActionPlan = {
  account: {
    slug: string;
    tenant_host: string;
    ingest_url: string;
    token_embedded: boolean;
    has_ingest_token: boolean;
  };
  agent: { id: string; name: string | null; active_version: string | null };
  channel: { id: string; label: string; routing_identifier: string | null } | null;
  agent_channels: { id: string; label: string; routing_identifier: string | null; dialable: boolean }[];
  fields: {
    name: string;
    type: string;
    /** Archived on the tenant. Still returned by 8loop; never a candidate. */
    archived: boolean;
    set_on_conversation_start: boolean;
    re_extract_on_conversation_end: boolean;
    allowed_values: string[] | null;
  }[];
  roles: {
    callback_time: CodeActionRoleMatch | null;
    disposition: CodeActionRoleMatch | null;
    contact_name: CodeActionRoleMatch | null;
  };
  /** Roles somebody said this agent has no field for, as opposed to ones nothing was found for. */
  roles_set_to_none: ("callback_time" | "disposition")[];
  missing: CodeActionProposedField[];
  /**
   * Instructions the callback-time field's prompt does not appear to give, stated as something
   * to merge into the tenant's own wording. Never a replacement: their prose is theirs, and on
   * a real tenant it is better than anything this app would generate — it was simply missing
   * the one sentence about 8loop that its author could not have known.
   */
  description_gaps: {
    role: "callback_time";
    field_id: string;
    field_name: string;
    gap: "relative_offset" | "language";
    missing: string;
    why: string;
    add: string;
  }[];
  disposition_map: {
    value: string;
    /** posted = ticked; not_a_callback = the app is sure it is not one; undecided = needs a person. */
    kind: "posted" | "not_a_callback" | "undecided";
    outcome: string | null;
    why: string;
    /** True when a person decided this one, rather than the app guessing it. */
    overridden?: boolean;
  }[];
  /** `patterns` means the field is free text, so the script classifies the phrase itself. */
  disposition_mode: "map" | "patterns";
  /**
   * What a callback dialled on this agent would carry, and whether this agent accepts each
   * name at call start. `settable: false` is the one that costs a call: 8loop refuses the whole
   * dial over a name it does not know, so the app drops it and rings without it.
   */
  context: {
    id: string;
    field_name: string;
    source: string;
    settable: boolean | null;
    account_wide: boolean;
  }[];
  /** What the ticks on the agent's page are asking the brief for. Nothing here is stored. */
  handover: {
    context_for: string[];
    create_context: string[];
    merge_wording: boolean;
    want_context: boolean;
  };
  webhook: {
    existing: { id: string; name: string; is_ours: boolean; is_code: boolean } | null;
    /** Our hook on the open draft: installed, and firing on nothing until it is published. */
    on_draft: { id: string; name: string } | null;
    /**
     * True when the installed hook is posting with an ingest token this account no longer
     * accepts — somebody rotated and did not hand over a fresh brief. The webhook looks
     * perfect and every call it makes gets a 401.
     */
    token_stale: boolean | null;
    others: { id: string; name: string; type: string }[];
  };
  draft: {
    exists: boolean;
    /** Webhooks the live version has and the draft does not — publishing would delete these. */
    webhooks_missing: string[];
    version: string | null;
    agent_has_published_version: boolean;
    blocks_install: boolean;
  };
  script: string;
  warnings: string[];
  ready: boolean;
};

export type CodeActionPlanResponse = { plan: CodeActionPlan; brief: string };

export type CodeActionScriptResponse = {
  plan: CodeActionPlan;
  script: string;
  brief: string;
  /** Only present when the token was rotated by this request. Never rendered twice. */
  ingest_token?: string;
  message: string;
};

/** One finished call on this agent, for testing the hook against something that happened. */
export type CodeActionRecentCall = {
  id: string;
  ended_at: string | null;
  end_reason: string | null;
  duration_seconds: number | null;
  /** Last four digits only — enough to recognise your own test call, and no more. */
  number_tail: string | null;
  direction: string | null;
};

export type CodeActionInstallStep = {
  step: string;
  status: "done" | "skipped" | "failed" | "would";
  detail: string;
};

export type CodeActionInstallResponse = {
  ok: boolean;
  dryRun: boolean;
  steps: CodeActionInstallStep[];
  plan: CodeActionPlan;
  /**
   * Present when the hook was written to the draft and left unpublished — the normal outcome
   * of an install since 2026-09-03. Its presence is what says "there is a hook here that
   * fires nothing yet", which the screen has to be able to state rather than imply.
   */
  staged?: { webhook_id: string | null; draft_version: string | null };
  verified: {
    on_active_version: boolean;
    webhook_id: string | null;
    version: string | null;
  } | null;
  message: string;
};

export type CodeActionTestResponse = {
  preview: boolean;
  ingest_url: string;
  result: {
    success: boolean;
    result?: Record<string, unknown> | null;
    captured_field_values?: Record<string, string>;
    logs?: { level?: string; message?: string; timestamp?: string }[];
    error_message?: string | null;
    duration_ms: number;
  };
  ingest_token?: string;
  plan: CodeActionPlan;
  message: string;
};

// ---------------------------------------------------------------- do not call ---

export type Suppression = {
  phone_number: string;
  /** Grouped for reading, e.g. `+91 95685 07028`. The raw number is what actions send. */
  phone_display: string;
  reason: string;
  note: string | null;
  created_at: string;
};

/**
 * A page of the do-not-call list, with the count of everything the filter matches.
 *
 * `total` is the filtered count, not the size of the list, so the pager stays honest inside
 * a search. Both matter, and conflating them is how the old version of this got the one
 * question the page exists to answer wrong: it returned a bare 500 rows with no total, so
 * number 501 read as "not suppressed" — silently, and in the one direction that hurts.
 */
export type SuppressionsResponse = {
  suppressions: Suppression[];
  total: number;
  limit: number;
  offset: number;
};

// ------------------------------------------------------ the CRM orchestrator ---

/**
 * The four facts that decide whether anything happens, on every orchestrator response.
 *
 * Two switches rather than one "will it dial", because they fail independently and the
 * answer to "why did nothing ring" is different for each: `orchestrator_enabled` off means
 * the timer never starts, `dial_mode` not live means every run assembles calls and places
 * none. Both are read from the environment at import time, so neither can be changed from
 * this app — which is itself something the screen has to say.
 */
export type OrchestratorSwitches = {
  orchestrator_enabled: boolean;
  dial_mode: "live" | "dry_run";
  can_dial_for_real: boolean;
  app_dry_run: boolean;
};

/** What the server is willing to publish about a customer's config — an allow-list. */
/** When an extra call happens: before the consent/eligibility rules, or just before dialling. */
export type LookupStage = "before_rules" | "before_dial";

/**
 * One extra API a feed calls, as the screen needs to describe it.
 *
 * Path and env-var name only — the base URL and every credential stay on the server, because a
 * page explaining what a feed does is not a place to publish where and how to authenticate.
 */
export type OrchestratorLookup = {
  /** The namespace its values land in: `as: "branch"` gives `branch.name`. */
  as: string;
  /** `table` fetches the whole reference list once; `by_key` asks per distinct key. */
  kind: "table" | "by_key";
  stage: LookupStage;
  /** The column holding the key it is asked by. */
  key_from: string;
  method: "GET" | "POST";
  path: string;
  base_url_env: string;
  /** True when a key it cannot answer takes the row out of the run. */
  required: boolean;
  /** The field names it contributes, fully qualified. */
  provides: string[];
};

export type OrchestratorCustomer = {
  customer: string;
  /** The feed's name — the job it does ("No-show follow-up"), not the account it dials for. */
  label: string;
  /** One line: who gets rung and when. Set by whoever configured the feed. */
  purpose: string;
  account_slug: string;
  agent_id: string;
  source_adapter: string;
  date_column: string;
  /** Pre-formatted by the server, e.g. `09:00–18:00 Asia/Kolkata` (en dash). */
  window: string;
  poll_every_minutes: number;
  /** False for a next-day batch, where the interval above means nothing. */
  polls_intraday: boolean;
  reconcile_at_open: boolean;
  call_after_event_minutes: number;
  max_event_age_minutes: number | null;
  max_dials_per_run: number;
  max_age_days: number;
  eligibility_rule: string;
  mapped_fields: string[];
  /**
   * The extra APIs this feed joins onto its list, in the order they run. Empty when one API
   * carries everything — which is the common case, and why the pipeline hides the step.
   */
  lookups: OrchestratorLookup[];
  /** The custom code step, when the feed has one. */
  derive: { summary: string; provides: string[]; stage: LookupStage } | null;
  /** Readable conditions, e.g. `DoNotCall = "1"`. Empty means the customer has no such gate. */
  suppress_when: string[];
  fetch_only_when: string[];
  /**
   * The account this feed dials through, resolved from `account_slug`.
   *
   * Null when the slug matches no account — a real misconfiguration, and the reason this is
   * nullable rather than assumed: such a feed is listed but cannot be filed under an account.
   */
  account_id: string | null;
  account_name: string | null;
};

export type OrchestratorHalt = {
  customer: string;
  halted_at: string;
  code: string | null;
  reason: string;
  run_id: string | null;
};

/**
 * A feed a person switched off, which is not the same fact as a halt.
 *
 * A halt is the feed stopping itself over something broken and waiting for somebody to look; a
 * pause is somebody deciding it should stop, with nothing wrong. Kept apart so a deliberate
 * pause does not read as an incident in the run history — see migration 017.
 */
export type OrchestratorPause = {
  customer: string;
  paused_at: string;
  paused_by: string | null;
  /** The pauser's own words, or null. Never generated. */
  note: string | null;
  paused_by_email: string | null;
};

/**
 * Every counter one run produced.
 *
 * Mirrored in full rather than as a `Record<string, number>` because the screen names
 * specific ones — a table column reading `counts.deferredWindow` off an index signature
 * compiles happily while being `undefined` if the server ever renames it.
 */
export type OrchestratorRunCounts = {
  rowsFetched: number;
  recordCount: number | null;
  pages: number;
  truncated: boolean;
  normalizeDropped: number;
  suppressed: number;
  suppressedFailClosed: number;
  ineligiblePermanent: number;
  ineligibleNotYet: number;
  duplicates: number;
  stale: number;
  eligible: number;
  backlog: number;
  dialed: number;
  replayed: number;
  deferredWindow: number;
  deferredBudget: number;
  deferredRateLimit: number;
  dropped: number;
  failed: number;
  alreadyHandled: number;
  expired: number;
  pacerWaitedMs: number;
  /** Written into the persisted jsonb alongside the counters, so both shapes carry them. */
  dropReasons?: Record<string, number>;
  eligibleReasons?: Record<string, number>;
  blankFields?: Record<string, number>;
  pooled?: Record<string, number>;
};

/** A row of `crm_runs`. `counts` is a jsonb blob, hence the partial type. */
export type OrchestratorRun = {
  id: string;
  poller: string;
  dry_run: boolean;
  started_at: string;
  finished_at: string | null;
  ok: boolean | null;
  error: string | null;
  halted_code: string | null;
  halted_reason: string | null;
  record_count: number | null;
  rows_fetched: number | null;
  counts: Partial<OrchestratorRunCounts>;
};

export type OrchestratorRecordStatus =
  | "claimed"
  | "dialed"
  | "deferred"
  | "dropped"
  | "failed";

/** One stored person-day. Carries the real number and the mapped field values. */
export type OrchestratorRecord = {
  id: string;
  record_id: string;
  occurrence_date: string;
  phone_e164: string;
  status: OrchestratorRecordStatus;
  attempt_n: number;
  fields: Record<string, string>;
  occurred_at: string | null;
  first_seen_at: string;
  last_seen_at: string;
  dialed_at: string | null;
  deferred_until: string | null;
  call_id: string | null;
  call_status: string | null;
  last_reason: string | null;
  last_error_code: string | null;
  poller: string | null;
  run_id: string | null;
};

export type OrchestratorRecordStats = {
  by_status: { status: OrchestratorRecordStatus; n: number }[];
  total: number;
  /** Rows the CRM handed us again and we did not re-store. The visible dedupe evidence. */
  reseen: number;
};

/**
 * Where one credential's value is coming from right now.
 *
 * `unreadable` is its own case rather than folded into `missing`: the row is there and the
 * fix is different — `CREDENTIAL_ENCRYPTION_KEY` changed under it, and re-entering the key
 * is the only way out. Telling somebody "missing" would send them to look for a key that
 * is already in the box.
 */
export type OrchestratorCredentialSource = "environment" | "stored" | "missing" | "unreadable";

/** One credential a feed says it needs, and what it currently has. Never the value. */
export type OrchestratorCredentialSlot = {
  /** The stored name, shared with the environment variable that overrides it. */
  slot: string;
  label: string;
  /** What breaks without it, in the reader's terms. */
  purpose: string;
  /** Where in the CRM to find it. Absent when the config's author did not say. */
  help?: string;
  required: boolean;
  source: OrchestratorCredentialSource;
  /** First 12 characters and the last 4 — enough to recognise, never enough to use. */
  masked: string | null;
  updated_at: string | null;
  updated_by_name: string | null;
};

export type OrchestratorCredentials = {
  slots: OrchestratorCredentialSlot[];
  /** Every *required* slot has a value. What the run buttons are enabled on. */
  ready: boolean;
};

export type OrchestratorListResponse = OrchestratorSwitches & {
  customers: (OrchestratorCustomer & {
    halt: OrchestratorHalt | null;
    pause: OrchestratorPause | null;
    last_run: OrchestratorRun | null;
    stats: OrchestratorRecordStats;
    /**
     * Whether this feed can read its CRM at all.
     *
     * On the list as well as the feed, because "never run" and "never been given its keys"
     * look identical in a table of runs, and only one of them is fixable from this screen.
     */
    credentials_ready: boolean;
    credentials_missing: string[];
  })[];
  /** How many feeds exist in total, so a filtered-to-nothing list can say so. */
  total_configured: number;
};

export type OrchestratorFeed = OrchestratorListResponse["customers"][number];

/**
 * The feed itself: its config, whether it is halted, and how the most recent run went.
 *
 * One run, not a list. This used to carry twenty, which meant opening the page fetched
 * twenty runs' worth of jsonb counters before anything was on screen, and after a few days
 * of a fifteen-minute poll — 36 runs a day — most of them were scrolled past unread. The
 * history is now its own paged request, so the header can say "last run went fine" without
 * paying for the archive.
 */
export type OrchestratorDetailResponse = OrchestratorSwitches & {
  customer: OrchestratorCustomer;
  halt: OrchestratorHalt | null;
  pause: OrchestratorPause | null;
  last_run: OrchestratorRun | null;
  /** On the response the screen already makes, because the run buttons depend on it. */
  credentials: OrchestratorCredentials;
};

/** What saving or clearing a credential answers with: the new state, plus what happened. */
export type OrchestratorCredentialsResponse = OrchestratorCredentials & {
  message: string;
  cleared?: boolean;
};

export type OrchestratorRunsResponse = OrchestratorSwitches & {
  runs: OrchestratorRun[];
  total: number;
  limit: number;
  offset: number;
};

/**
 * A page of stored records.
 *
 * `total` counts what the current status filter matches; `stats` counts every status
 * regardless of it, because the chips above the table are how you change the filter and
 * they cannot be built out of the filtered page.
 */
export type OrchestratorRecordsResponse = OrchestratorSwitches & {
  records: OrchestratorRecord[];
  stats: OrchestratorRecordStats;
  total: number;
  limit: number;
  offset: number;
};

export type OrchestratorRunResult = {
  runId: string | null;
  ok: boolean;
  halted: { code: string; reason: string } | null;
  error: string | null;
  counts: OrchestratorRunCounts;
  dropReasons: Record<string, number>;
  eligibleReasons: Record<string, number>;
  blankFields: Record<string, number>;
  pooled: Record<string, number>;
  /**
   * Every dial a dry run would have placed, up to 500 of them. Empty on a real run.
   *
   * This is the list, not a taster of it — a dry run is the only pass some feeds will ever
   * get, so "the list it gets and what calls are made" has to be answerable from here.
   */
  planned: PlannedDial[];
  /** How many it planned in total. Differs from `planned.length` only past the 500 cap. */
  plannedTotal: number;
  /**
   * When the plan is what would go out at the **next window open** rather than on this pass:
   * the ISO instant of that open. Null when the run was inside its calling window.
   *
   * A dry run outside calling hours defers every row, and it used to return no plan at all —
   * so the screen showed a funnel with "122 outside calling hours" and no list. The list is
   * always there now; this is what lets the screen say when those calls would actually go.
   */
  plannedDeferredUntil: string | null;
};

/** One call a dry run would place, with the exact conversation fields it would send. */
export type PlannedDial = {
  recordId: string;
  /**
   * In full, as it would be dialled. The reason it is not masked is that the only thing this
   * list is for is checking these are the right people on the right numbers, and the same
   * reason the stored-records table shows `phone_e164` in full: an admin reading their own
   * tenant's rows. A dry run stores nothing, and run history keeps only the masked form.
   */
  phone: string;
  /** The same number masked. Kept for the run-history sample, not shown on this screen. */
  phoneMasked: string;
  occurrenceDate: string;
  occurredAt: string | null;
  fields: Record<string, string>;
};

export type OrchestratorRunResponse = OrchestratorSwitches & {
  run: OrchestratorRunResult;
  message: string;
};

export type OrchestratorPreset =
  | "opt-today"
  | "opt-yesterday"
  | "opt-last-seven-days"
  | "opt-all-time";

/** One agent channel this account owns, as the CRM feed's picker sees it. */
export type OrchestratorChannel = {
  channel_id: string;
  routing_identifier: string | null;
  label: string;
  agent_id: string | null;
  agent_name: string | null;
  /**
   * False for a channel 8loop will not originate on — a campaign dialler, typically. Returned
   * rather than filtered out: "why isn't my channel listed" is a worse question than "why is
   * that one greyed out".
   */
  dialable: boolean;
  blocked_reason: string | null;
};

export type OrchestratorChannelsResponse = {
  channels: OrchestratorChannel[];
  chosen: {
    routing_identifier: string;
    channel_id: string | null;
    channel_label: string | null;
    set_at: string;
  } | null;
  /** What the feed's config file says, so the reader can see what clearing goes back to. */
  from_config: string | null;
  env_override: string | null;
  refresh_error: string | null;
};
